import * as React from 'react';

import { AddressType } from '@rematter/frontend-lib-api-client';
import { Button } from '@rematter/ui-web';
import { useToast } from '@rematter/ui-web/hooks';
import { useAtomValue, useSetAtom } from 'jotai';

import { AccountRecord } from '../../_graphql/queryAccountsByIds';
import { $updateSalesOrder } from '../../_state/salesOrderDetail.state';
import { $accountsByIdQueryLazy } from '../../_state/salesOrderForm.state';
import { useTranslations } from '../../i18n/useTranslations';
import { SalesDetailLayout } from '../_layouts/SalesDetailLayout';

import { LabeledText } from './LabeledText';
import {
  LocationFormValues,
  SalesOrderTabCustomerInfoLocationForm,
} from './SalesOrderTabCustomerInfoLocationForm';
import { SalesOrderTabCustomerSectionContacts } from './SalesOrderTabCustomerSectionContacts';
import { SalesOrderTabCustomerSectionCustomAttributes } from './SalesOrderTabCustomerSectionCustomAttributes';
import { SalesOrderTabCustomerSectionInfo } from './SalesOrderTabCustomerSectionInfo';

import { SalesOrderRecord } from '~/modules/trade/types/salesOrder';

type Props = {
  salesOrder?: SalesOrderRecord | null;
  onSaveUpdates?: () => void;
  onDiscard?: () => void;
  disabled?: boolean;
  variant?: 'default' | 'embedded';
};

const salesOrderToLocationFormValues = (
  salesOrder: SalesOrderRecord,
): LocationFormValues => {
  const sameAsShip =
    salesOrder.shippingAddress?.streetAddress ===
      salesOrder.billingAddress?.streetAddress &&
    salesOrder.shippingAddress?.city === salesOrder.billingAddress?.city;

  return {
    shipToLocationId: null,
    billToLocationId: null,
    sameAsShip,
    shippingAddress: salesOrder.shippingAddress
      ? {
          addressType: salesOrder.shippingAddress.addressType,
          streetAddress: salesOrder.shippingAddress.streetAddress,
          city: salesOrder.shippingAddress.city,
          state: salesOrder.shippingAddress.state,
          postalCode: salesOrder.shippingAddress.postalCode,
          country: salesOrder.shippingAddress.country,
        }
      : null,
    billingAddress: salesOrder.billingAddress
      ? {
          addressType: salesOrder.billingAddress.addressType,
          streetAddress: salesOrder.billingAddress.streetAddress,
          city: salesOrder.billingAddress.city,
          state: salesOrder.billingAddress.state,
          postalCode: salesOrder.billingAddress.postalCode,
          country: salesOrder.billingAddress.country,
        }
      : null,
  };
};

export const SalesOrderTabCustomer: React.FC<Props> = ({
  salesOrder,
  onSaveUpdates,
  onDiscard,
  disabled = false,
  variant = 'default',
}) => {
  const t = useTranslations();
  const toast = useToast();

  const updateSalesOrderMutation = useSetAtom($updateSalesOrder);
  const [isSaving, setIsSaving] = React.useState(false);

  const [locationFormData, setLocationFormData] =
    React.useState<LocationFormValues | null>(null);
  const [originalLocationData, setOriginalLocationData] =
    React.useState<LocationFormValues | null>(null);

  React.useEffect(() => {
    if (salesOrder) {
      const formValues = salesOrderToLocationFormValues(salesOrder);
      setLocationFormData(formValues);
      setOriginalLocationData(formValues);
    } else {
      setLocationFormData(null);
      setOriginalLocationData(null);
    }
  }, [salesOrder]);

  const { data: accountsQueryData, fetch: fetchAccountsQuery } = useAtomValue(
    $accountsByIdQueryLazy,
  );

  React.useEffect(() => {
    if (salesOrder?.account?.id) {
      fetchAccountsQuery({
        accountIds: [salesOrder.account.id],
      });
    }
  }, [salesOrder?.account?.id, fetchAccountsQuery]);

  const selectedAccount = React.useMemo(
    () =>
      accountsQueryData?.find(
        (account) => account.id === salesOrder?.account?.id,
      ),
    [accountsQueryData, salesOrder?.account?.id],
  ) as AccountRecord;

  React.useEffect(() => {
    if (salesOrder && locationFormData && selectedAccount) {
      const addresses = selectedAccount.addresses ?? [];

      const matchingShipToAddress = addresses.find(
        (addr) =>
          addr.address.streetAddress ===
            salesOrder.shippingAddress?.streetAddress &&
          addr.address.city === salesOrder.shippingAddress?.city &&
          addr.address.state === salesOrder.shippingAddress?.state &&
          addr.address.postalCode === salesOrder.shippingAddress?.postalCode,
      );

      const matchingBillToAddress = addresses.find(
        (addr) =>
          addr.address.streetAddress ===
            salesOrder.billingAddress?.streetAddress &&
          addr.address.city === salesOrder.billingAddress?.city &&
          addr.address.state === salesOrder.billingAddress?.state &&
          addr.address.postalCode === salesOrder.billingAddress?.postalCode,
      );

      const updates: Partial<LocationFormValues> = {};

      if (matchingShipToAddress) {
        updates.shipToLocationId = matchingShipToAddress.id;
      }

      if (matchingBillToAddress) {
        updates.billToLocationId = matchingBillToAddress.id;
      }

      if (Object.keys(updates).length > 0) {
        setLocationFormData((prev) => (prev ? { ...prev, ...updates } : prev));
        setOriginalLocationData((prev) =>
          prev ? { ...prev, ...updates } : prev,
        );
      }
    }
  }, [salesOrder, selectedAccount]);

  const isLocationFormChanged = React.useMemo(
    () =>
      locationFormData &&
      originalLocationData &&
      JSON.stringify(locationFormData) !== JSON.stringify(originalLocationData),
    [locationFormData, originalLocationData],
  );

  const handleLocationFormChange = React.useCallback(
    (
      updater:
        | LocationFormValues
        | ((prev: LocationFormValues) => LocationFormValues),
    ) => {
      setLocationFormData((prev) => {
        if (!prev) return prev;
        return typeof updater === 'function' ? updater(prev) : updater;
      });
    },
    [],
  );

  const handleSave = React.useCallback(async () => {
    if (!salesOrder || !locationFormData) return;

    try {
      setIsSaving(true);

      const updateData = {
        id: salesOrder.id,
        shippingAddress: locationFormData.shippingAddress
          ? {
              addressType: locationFormData.shippingAddress
                .addressType as AddressType,
              streetAddress: locationFormData.shippingAddress.streetAddress,
              city: locationFormData.shippingAddress.city,
              state: locationFormData.shippingAddress.state,
              postalCode: locationFormData.shippingAddress.postalCode,
              country: locationFormData.shippingAddress.country,
              isMailingAddress: true,
            }
          : undefined,
        billingAddress: locationFormData.billingAddress
          ? {
              addressType: locationFormData.billingAddress
                .addressType as AddressType,
              streetAddress: locationFormData.billingAddress.streetAddress,
              city: locationFormData.billingAddress.city,
              state: locationFormData.billingAddress.state,
              postalCode: locationFormData.billingAddress.postalCode,
              country: locationFormData.billingAddress.country,
              isMailingAddress: true,
            }
          : undefined,
      };

      await updateSalesOrderMutation(updateData);

      toast.success(t.salesOrderDetail.messages.locationUpdated);
      setOriginalLocationData({ ...locationFormData });
      onSaveUpdates?.();
    } catch (error) {
      console.error('Failed to update customer location:', error);
      toast.error(t.salesOrderDetail.messages.locationUpdateError);
    } finally {
      setIsSaving(false);
    }
  }, [
    salesOrder,
    locationFormData,
    updateSalesOrderMutation,
    toast,
    t,
    onSaveUpdates,
  ]);

  const handleDiscard = React.useCallback(() => {
    if (originalLocationData) {
      setLocationFormData({ ...originalLocationData });
    }
    onDiscard?.();
  }, [originalLocationData, onDiscard]);

  if (!salesOrder) {
    return (
      <div className="p-6">
        <div className="rounded-lg bg-white p-6">
          <div className="py-8 text-center">
            <h3 className="mb-2 text-lg font-semibold text-neutral">
              {t.salesOrderDetail.customer.loading}
            </h3>
          </div>
        </div>
      </div>
    );
  }

  return (
    <SalesDetailLayout
      className="@SalesOrderTabCustomer"
      variant={variant}
      footer={
        !disabled && (
          <div className="flex flex-shrink-0 items-center gap-3">
            <Button
              variant="outline"
              onPress={handleDiscard}
              isDisabled={isSaving}
            >
              {t.salesOrderDetail.actions.discard}
            </Button>
            <Button
              variant="solid"
              color="primary"
              onPress={handleSave}
              isPending={isSaving}
              isLoading={isSaving}
              isDisabled={!isLocationFormChanged || !salesOrder}
            >
              {t.salesOrderDetail.actions.saveUpdates}
            </Button>
          </div>
        )
      }
    >
      <SalesOrderTabCustomerSectionInfo
        title={t.salesOrderDetail.customer.sections.contractInfo}
      >
        {locationFormData && (
          <SalesOrderTabCustomerInfoLocationForm
            disabled={disabled || isSaving}
            selectedAccount={selectedAccount}
            formData={locationFormData}
            onFormDataChange={handleLocationFormChange}
          />
        )}
      </SalesOrderTabCustomerSectionInfo>

      <SalesOrderTabCustomerSectionInfo
        title={t.salesOrderDetail.customer.sections.profile}
      >
        <div className="mt-4 grid grid-cols-3 gap-4">
          <LabeledText
            label={t.salesOrderDetail.customer.profile.companyType}
            text={t.salesOrderDetail.customer.profile.supplierCustomer}
          />
          <LabeledText
            label={t.salesOrderDetail.customer.profile.companyName}
            text={salesOrder.account?.name}
          />
          <LabeledText
            label={t.salesOrderDetail.customer.profile.contactName}
            text={salesOrder.contact?.fullName}
          />
          <LabeledText
            label={t.salesOrderDetail.customer.profile.payeeName}
            text={'N/A'}
          />
          <LabeledText
            label={t.salesOrderDetail.customer.profile.customCompanyId}
            text={'N/A'}
          />
          <LabeledText
            label={t.salesOrderDetail.customer.profile.businessLicenseNumber}
            text={'N/A'}
          />
        </div>
      </SalesOrderTabCustomerSectionInfo>

      <SalesOrderTabCustomerSectionCustomAttributes salesOrder={salesOrder} />

      <SalesOrderTabCustomerSectionContacts selectedAccount={selectedAccount} />

      <SalesOrderTabCustomerSectionInfo
        title={t.salesOrderDetail.customer.sections.pricingPreferences}
      >
        <div className="mt-4 grid grid-cols-3 gap-4">
          <LabeledText
            label={t.salesOrderDetail.customer.pricing.priceListGroup}
            text={'N/A'}
          />
          <LabeledText
            label={t.salesOrderDetail.customer.pricing.inboundTaxRate}
            text={'N/A'}
          />
          <LabeledText
            label={t.salesOrderDetail.customer.pricing.outboundTaxRate}
            text={'N/A'}
          />
        </div>
      </SalesOrderTabCustomerSectionInfo>

      <SalesOrderTabCustomerSectionInfo
        title={t.salesOrderDetail.customer.sections.paymentPreferences}
      >
        <div className="mt-4 grid grid-cols-3 gap-4">
          <LabeledText
            label={t.salesOrderDetail.customer.pricing.defaultPaymentType}
            text={'N/A'}
          />
          <LabeledText
            label={t.salesOrderDetail.customer.pricing.defaultPaymentSchedule}
            text={salesOrder.paymentTerm}
          />
        </div>
      </SalesOrderTabCustomerSectionInfo>
    </SalesDetailLayout>
  );
};
